import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation, useNavigationType } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import Swal from "sweetalert2";
import SuccessAlert from "./../child/Alert/SuccessAlert";
import DengerAlert from "./../child/Alert/DengerAlert";
import {
  deleteVaultQuestionById,
  deleteVaultQuestionsBulkByIds,
  fetchVaultQuestionsExport,
  fetchAllVaultQuestions,
  toggleVaultQuestionStatus,
  fetchBulkVaultQuestions,
  generateAIQuestions,
  fetchAiReview,
  bulkReviewThunk,
  publishQuestion,
} from "../../redux/vaultQuestions/vaultQuestionThunks";
import {
  setFilters,
  setParsedRows,
  setParseErrors,
  setValidateErrors,
} from "../../redux/vaultQuestions/vaultQuestionSlice";
import {
  showConfirmationDialog,
  showErrorMessage,
  showInfoDialog,
  showRejectWithEditOption,
  showSuccessMessage,
} from "../../utils/apiHelper";
import * as XLSX from "xlsx";
import { parseFile } from "../../utils/parseFile";
import AIQuestionPromptModal from "./AIQuestionPromptModal";
import { getReviewButtonProps } from "../../utils/reviewButtonProps";
import ReviewButton from "./ReviewButton";
import moment from "moment";
import { getModulePermissions } from "../../utils/permissionUtils";
import { publishVaultQuestion, reviewVaultQuestion } from "../services/vaultQuestionService";
import BulkPublishButton from "./BulkPublishButton";
import { listFiltersFromSearch, statusDropdownValue, declineReasonPlaceholder, isMemberApprovalReply } from "./memberQuestionUtils";
import { AskedByInline, AskedByTimes } from "./AskedBy";

const VaultQuestionsListLayer = () => {
  const dispatch = useDispatch();
  const fileRef = useRef();
  const navigate = useNavigate();
  const {
    shouldCheckPermissions,
    canEdit,
    canAdd,
    canView,
    canDelete,
    canReview,
  } = getModulePermissions("vault-questions");

  const {
    questionList: items,
    totalPages,
    totalQuestions: totalItems,
    filters: {
      currentPage,
      itemsPerPage,
      searchQuery,
      sortColumn,
      sortOrder,
      statusFilter,
      tierFilter,
      reviewStatusFilter,
      isPublishedFilter,
      isActiveFilter,
    },
    loading,
    exportLoading,
    parsedRows,
    parseErrors,
    validateErrors,
    saveResult,
    saveError,
  } = useSelector((state) => state.vaultQuestion);
  // The Status drop-down's value; both pending keys read "Pending AI
  // Review" too (memberQuestionUtils, 2026-11-03).
  const combinedFilterValue = statusDropdownValue({ statusFilter, isPublishedFilter });

  const location = useLocation();

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [exportType, setExportType] = useState("");
  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const authToken = localStorage.getItem("authToken");
  const headerCheckboxRef = useRef(null);

  const fetchData = (page) => {
    let statusParam;

    if (statusFilter === "all") {
      statusParam = undefined; // fetch all statuses
    } else if (statusFilter === "pending_ai_review") {
      // include both pending keys
      statusParam = "pending_ai_review,pending_review";
    } else {
      statusParam = statusFilter; // published, ai_approved, ai_rejected
    }
    const params = {
      page: page,
      limit: itemsPerPage,
      search: searchQuery,
      sortBy: sortColumn,
      sortOrder: sortOrder,
      status: statusParam,
      reviewStatus:
        reviewStatusFilter !== "all" ? reviewStatusFilter : undefined,
      isPublished: isPublishedFilter !== "all" ? isPublishedFilter : undefined,
      isActive: isActiveFilter !== "all" ? isActiveFilter : undefined,
      tierFilter: tierFilter || undefined,
    };
    dispatch(fetchAllVaultQuestions({ params, navigate }));
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [
    dispatch,
    navigate,
    currentPage,
    searchQuery,
    itemsPerPage,
    sortOrder,
    sortColumn,
    statusFilter,
    tierFilter,
    reviewStatusFilter,
    isPublishedFilter,
    isActiveFilter,
  ]);

  // The filters from the address: status and isPublished as before, and
  // (2026-11-03) tier and reviewStatus, so the bell's member question row
  // opens the member questions waiting for review (memberQuestionUtils).
  // A link starts on page 1; Back keeps the page the admin was on.
  const navigationType = useNavigationType();
  useEffect(() => {
    dispatch(setFilters(listFiltersFromSearch(location.search, { navigationType })));
  }, [location.search, navigationType, dispatch]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      dispatch(setFilters({ currentPage: pageNumber }));
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    dispatch(setFilters({ sortColumn: column, sortOrder: newOrder }));
  };

  const handleItemsPerPageChange = (e) => {
    dispatch(
      setFilters({ itemsPerPage: parseInt(e.target.value, 10), currentPage: 1 })
    );
  };

  // const handleSelectAll = (e) => {
  //   if (e.target.checked) {
  //     setSelectedItems(items.map((item) => item._id));
  //   } else {
  //     setSelectedItems([]);
  //   }
  // };

  //   const handleSelectItem = (id) => {
  //   setSelectedItems((prevSelected) => {
  //     if (prevSelected.includes(id)) {
  //       return prevSelected.filter((item) => item !== id);
  //     } else {
  //       return [...prevSelected, id];
  //     }
  //   });
  // };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4); // Ensure only 5 items are shown (start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      dispatch(setFilters({ currentPage: currentPage + 1 }));
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(setFilters({ currentPage: currentPage - 1 }));
    }
  };

  const handleDeleteVaultQuestion = async (id) => {
    const confirmMessage = "Delete";
    const cancelMessage = "Cancel";

    const result = await showConfirmationDialog(
      "This action will delete the Vault Question!",
      confirmMessage,
      cancelMessage
    );

    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(
        deleteVaultQuestionById({ id, navigate })
      ).unwrap();
      setSuccessMessage(data?.message || "Question deleted successfully");
      if (items.length === 1 && currentPage > 1) {
        dispatch(setFilters({ currentPage: currentPage - 1 }));
      } else {
        fetchData(currentPage);
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to delete question";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    const result = await showConfirmationDialog(
      `Are you sure you want to ${
        newStatus ? "activate" : "inactivate"
      } this question?`,
      `Yes, ${newStatus ? "activate" : "inactivate"} it!`
    );

    if (!result.isConfirmed) return;

    try {
      const res = await dispatch(
        toggleVaultQuestionStatus({
          id,
          data: { isActive: newStatus },
          navigate,
        })
      ).unwrap();

      // ✅ Only show success if backend actually returned success
      if (res?.statusCode === 200) {
        setSuccessMessage(
          res?.message ||
            `Vault question ${
              newStatus ? "activated" : "inactivated"
            } successfully`
        );
      } else {
        setErrorMessage(res?.error || "Failed to change status");
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to change status";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : Array.from(selectedItems);
    if (idsToDelete.length === 0) return;

    // Dynamic pluralization based on number of IDs
    const questionText = `Vault Question${idsToDelete.length > 1 ? "s" : ""}`;
    const message = `This action will delete the ${questionText}!`;

    // Dynamic confirm/cancel buttons
    const confirmMessage = idsToDelete.length > 1 ? "Delete all" : "Delete";
    const cancelMessage = "Cancel";

    const result = await showConfirmationDialog(
      message,
      confirmMessage,
      cancelMessage
    );

    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(
        deleteVaultQuestionsBulkByIds({ ids: idsToDelete, navigate })
      );

      if (!data.payload || !Array.isArray(data.payload)) {
        throw new Error(`Deletion failed or unexpected response.`);
      }

      const successMessage =
        single === "Yes"
          ? "Vault Question deleted successfully"
          : "Vault Questions deleted successfully";

      setSuccessMessage(successMessage);
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      if (single === "No") {
        // setSelectedItems([]);
        setSelectedItems(new Set());
      }

      // ---- Pagination fix here ----
      const deletedCount = data?.deletedCount ?? idsToDelete.length;

      const remainingTotal = Math.max(totalItems - deletedCount, 0);
      const newTotalPages = Math.max(
        Math.ceil(remainingTotal / itemsPerPage),
        1
      );

      let nextPage = currentPage;

      // If after deletion, current page is out of range → go to last valid page
      if (nextPage > newTotalPages) {
        nextPage = newTotalPages;
      }

      if (items.length === idsToDelete.length) {
        if (currentPage === 1) {
          nextPage = 1;
        } else {
          nextPage = Math.max(nextPage - 1, 1);
        }
      }

      // ✅ Update filters so useEffect will refetch correct page
      dispatch(setFilters({ currentPage: nextPage }));
      fetchData(nextPage);
    } catch (error) {
      setErrorMessage(error.message || "An error occurred");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleExport = () => {
    if (!exportType) return;

    const filters = {
      search: searchQuery,
      status: statusFilter,
      tier: tierFilter,
      sortColumn,
      sortOrder,
    };

    dispatch(
      fetchVaultQuestionsExport({
        format: exportType, // ✅ backend expects `format`
        filters,
      })
    );
  };

  // const isSelected = (id) => selectedItems.includes(id);
  const isSelected = (id) => selectedItems.has(id);

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) updated.delete(id);
      else updated.add(id);
      return updated;
    });
  };

  // Select/deselect all on current page
  const handleSelectAll = (checked) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      items.forEach((user) => {
        if (checked) updated.add(user._id);
        else updated.delete(user._id);
      });
      return updated;
    });
  };

  // Header checkbox helpers
  const currentPageIds = items.map((u) => u._id);
  const isHeaderChecked = () =>
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedItems.has(id));
  const isHeaderIndeterminate = () =>
    currentPageIds.some((id) => selectedItems.has(id)) && !isHeaderChecked();

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isHeaderIndeterminate();
    }
  }, [items, selectedItems]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.warn("No file selected");
      return;
    }

    // Parse file
    const {
      rows = [],
      headers,
      errors: parseErrors = [],
    } = await parseFile(file);

    // Dispatch parse errors (if any)
    if (parseErrors.length > 0) {
      console.warn("Parse errors found:", parseErrors);
      dispatch(setParseErrors(parseErrors));
    } else {
      dispatch(setParseErrors([]));
    }

    // Empty file check
    if (!rows.length) {
      const errMsg = "The uploaded file contains no data rows.";
      setErrorMessage(errMsg);
      console.error(errMsg);
      dispatch(
        setValidateErrors([
          {
            line: 1,
            messages: [errMsg],
          },
        ])
      );
      return;
    }

    // Use headers from parseFile or infer from first row keys
    const actualHeaders =
      headers && headers.length > 0
        ? headers
        : rows.length > 0
        ? Object.keys(rows[0])
        : [];

    // Expected headers
    const expectedHeaders = [
      "tier",
      "questionText",
      "questionType",
      "answers",
      "tags",
      "isActive",
      "reviewStatus",
      "aiSource",
      "notesInternal",
    ];

    // Validate headers (case-insensitive)
    const missingHeaders = expectedHeaders.filter(
      (h) =>
        !actualHeaders.some(
          (uh) => uh.trim().toLowerCase() === h.trim().toLowerCase()
        )
    );

    if (missingHeaders.length > 0) {
      const errMsg = `Missing required headers: ${missingHeaders.join(", ")}`;
      console.error(errMsg);

      setErrorMessage(
        `Invalid template! Some required headers are missing in your file.`
      );
      // setTimeout(() => setErrorMessage(""), 5000);

      dispatch(
        setValidateErrors([
          {
            line: 1,
            messages: [errMsg],
          },
        ])
      );
      return;
    }

    // Normalize rows
    const normalizeRow = (rawRow) => {
      const get = (name) => {
        const foundKey = Object.keys(rawRow).find(
          (k) => k.trim().toLowerCase() === name.trim().toLowerCase()
        );
        return foundKey ? rawRow[foundKey] : "";
      };

      let reviewStatus = get("reviewStatus");
      let isActive = get("isActive");

      // Enforce rules based on reviewStatus
      if (reviewStatus === "approved") {
        isActive = "true";
      } else if (
        reviewStatus === "pending" ||
        reviewStatus === "rejected" ||
        !reviewStatus
      ) {
        isActive = "false";
      }
      return {
        questionText: get("questionText"),
        answers: get("answers"),
        tier: get("tier"),
        tags: get("tags"),
        // isActive: get("isActive"),
        isActive,
        questionType: get("questionType"),
        // reviewStatus: get("reviewStatus"),
        reviewStatus,
        aiSource: get("aiSource"),
        notesInternal: get("notesInternal"),
        __raw: rawRow,
      };
    };

    const normalized = rows.map((r) => normalizeRow(r));

    // Row-level validation
    const validateRows = (rowsToValidate) => {
      const errors = [];
      const seen = new Map();

      rowsToValidate.forEach((row, idx) => {
        const line = idx + 2;
        const rowErrs = [];

        if (!row.questionText?.trim()) {
          rowErrs.push("Question Text is required");
        }
        if (!row.answers?.trim()) {
          rowErrs.push("Answer Options are required");
        }
        if (!row.tier?.trim()) {
          rowErrs.push("Tier is required");
        }

        const isActiveRaw = row.IsActive;
        if (isActiveRaw !== "" && isActiveRaw !== undefined) {
          const v = String(isActiveRaw).trim().toLowerCase();
          if (!["true", "false", "yes", "no", "1", "0"].includes(v)) {
            rowErrs.push("Is Active must be true/false");
          }
        }

        const qkey = row.questionText?.trim().toLowerCase();
        if (qkey) {
          if (seen.has(qkey)) {
            rowErrs.push(`Duplicate of row ${seen.get(qkey)}`);
          } else {
            seen.set(qkey, line);
          }
        }

        if (rowErrs.length) {
          errors.push({ line, messages: rowErrs });
        }
      });

      return errors;
    };

    const validationErrors = validateRows(normalized);

    if (validationErrors.length > 0) {
      setErrorMessage("Row validation errors found");
      console.warn("Row validation errors found:", validationErrors);
    } else {
      console.log("Row validation passed with no errors");
    }

    // Save parsed rows + errors to state
    dispatch(setParsedRows(normalized));
    dispatch(setValidateErrors(validationErrors));

    // Only navigate if no critical errors (empty rows or missing headers)
    // If you want to navigate even with row validation errors, you can remove this check
    if (validationErrors.length === 0) {
      // console.log("Navigation to /vault-questions/bulk-edit");
      // navigate("/vault-questions/bulk-edit", { state: { fileName: file.name } });
    } else {
      console.warn(
        "Not navigating due to row validation errors. Fix errors to proceed."
      );
    }
    navigate("/vault-questions/bulk-edit", { state: { fileName: file.name } });
  };

  const handleBulkEdit = async () => {
    // if (!selectedItems.length) return;

    const selectedArray = Array.from(selectedItems); // convert Set -> Array
    if (!selectedArray.length) return;

    try {
      const { data } = await dispatch(
        fetchBulkVaultQuestions(selectedArray)
      ).unwrap();
      // Adapt server data to match "normalized rows" structure from file upload
      const normalized = data.map((q) => ({
        questionText: q.questionText || "",
        answers: (q.answers || []).join("; "),
        tier: q.tier || "",
        tags: (q.tags || []).join("; "),
        isActive: q.isActive ? "true" : "false",
        questionType: q.questionType || "",
        reviewStatus: q.reviewStatus || "",
        aiSource: q.aiSource || "",
        notesInternal: q.notesInternal || "",
        __raw: q,
      }));

      dispatch(setParsedRows(normalized));
      dispatch(setValidateErrors([])); // Or run same validation function if needed

      navigate("/vault-questions/bulk-edit", { state: { from: "bulk" } });
    } catch (err) {
      console.error("Bulk edit fetch failed:", err);
    }
  };

  const handleGenerateAI = async (topic) => {
    const aiData = await dispatch(generateAIQuestions(topic)).unwrap();
    // aiData = { overview: {...}, questions: [...] }

    const sensitivityLevel = aiData.overview?.sensitivity_level || "";
    const aiScore = aiData.overview?.ai_score || "";
    const flag = aiData.overview?.flag === true;

    const normalized = (aiData.questions || []).map((q) => ({
      questionText: q.question || "",
      answers: (q.answers || []).map((a) => a.answer).join("; "),
      tier: "",
      tags: "",
      questionType: "",
      notesInternal: "",
      sensitivityLevel,
      aiSource: "ai",
      aiScore,
      status: flag ? "ai_approved" : "",
      reviewStatus: flag ? "approved" : "",
      isActive: flag ? true : "true",

      __raw: q,
    }));

    dispatch(setParsedRows(normalized));
    dispatch(setValidateErrors([]));

    setShowModal(false);
    navigate("/vault-questions/bulk-edit", { state: { from: "ai" } });
  };

  const handleAiReview = async (item) => {
    try {
      // Show loading
      Swal.fire({
        title: "Running AI Review...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const data = await dispatch(fetchAiReview(item._id)).unwrap();
      Swal.close(); // close loader

      if (data?.status === 1) {
        const updated = data.updated;
        if (updated?.reviewStatus === "approved") {
          // ✅ AI approved → just notify
          setSuccessMessage(
            updated?.flaggedReason || "AI Review approved this question ✅"
          );
        } else {
          const goToEdit = await showRejectWithEditOption(
            updated?.flaggedReason
          );

          if (goToEdit.isConfirmed) {
            Swal.close();
            setTimeout(() => {
              navigate(`/vault-questions/edit-vault-question/${updated._id}`);
            }, 0);
          }
        }
      } else {
        setErrorMessage(data?.message || "Failed to fetch AI review.");
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      Swal.close();
      setErrorMessage("Unexpected error while running AI review.");
    }
  };

  const handleBulkReview = async () => {
    try {
      
      const idsToReview = Array.from(selectedItems);
      if (idsToReview.length === 0) {
        setErrorMessage("Please select at least one question for review");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      const MAX_BULK_REVIEW = 20;
      if (idsToReview.length > MAX_BULK_REVIEW) {
        setErrorMessage(
          `You can only review up to ${MAX_BULK_REVIEW} questions at a time.`
        );
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      const resultAction = await dispatch(
        bulkReviewThunk(idsToReview)
      );

      if (bulkReviewThunk.fulfilled.match(resultAction)) {
        const { results } = resultAction.payload;

        if (!results || results.length === 0) {
          setSuccessMessage(
            "All selected questions are already reviewed. Please select unreviewed questions."
          );
          // Clear message after 3 seconds
          setTimeout(() => {
            setSuccessMessage("");
          }, 3000);
          return;
        }

        let successCount = 0;
        let errorCount = 0;
        const rejectedIds = [];

        results.forEach((r) => {
          if (r.success && r.updatedDoc.reviewStatus === "approved")
            successCount += 1;
          if (r.success && r.updatedDoc.reviewStatus === "rejected")
            rejectedIds.push(r.id);
          if (!r.success) errorCount += 1;
        });

        // Show toasts
        let toastMessage = "";
        if (successCount && errorCount) {
          toastMessage = `✅ ${successCount} question(s) reviewed successfully, ❌ ${errorCount} question(s) failed to review.`;
        } else if (successCount) {
          toastMessage = `✅ ${successCount} question(s) reviewed successfully.`;
        } else if (errorCount) {
          toastMessage = `❌ ${errorCount} question(s) failed to review.`;
        }

        if (toastMessage) {
          setSuccessMessage(toastMessage);

          // ✅ Clear selection after successful bulk review
          setSelectedItems(new Set());
        }
        // If there are rejected questions, show confirmation modal before navigating
        if (rejectedIds.length > 0) {
          const message = `There are ${rejectedIds.length} question(s) rejected by AI. Do you want to edit them now?`;
          const confirmMessage = "Yes, edit them!";
          const result = await showConfirmationDialog(message, confirmMessage);

          if (result.isConfirmed) {
            // Fetch rejected questions for bulk edit
            const { data } = await dispatch(
              fetchBulkVaultQuestions(rejectedIds)
            ).unwrap();

            const normalized = data.map((q) => ({
              questionText: q.questionText || "",
              answers: (q.answers || []).join("; "),
              tier: q.tier || "",
              tags: (q.tags || []).join("; "),
              isActive: q.isActive ? "true" : "false",
              questionType: q.questionType || "",
              reviewStatus: q.reviewStatus || "",
              aiSource: q.aiSource || "",
              notesInternal: q.notesInternal || "",
              __raw: q,
            }));

            dispatch(setParsedRows(normalized));
            dispatch(setValidateErrors([]));

            navigate("/vault-questions/bulk-edit", { state: { from: "bulk" } });
          }
        }
      } else {
        setErrorMessage(resultAction.payload?.message || "Bulk review failed");
      }
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong during bulk review");
    }
  };

  const handlePublish = async (item) => {
    try {
      Swal.fire({
        title: "Publishing question...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      // ✅ Dispatch the Redux thunk, NOT the raw API function
      const res = await dispatch(publishQuestion({ id: item._id })).unwrap();

      Swal.close();

      if (res.status === 1) {
        setSuccessMessage("Question published successfully ✅");

        // Optional: refresh table from server
      } else {
        setErrorMessage(res.message || "Failed to publish question");
        setTimeout(() => setErrorMessage(""), 3000);
      }
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
        fetchData(currentPage);
      }, 1000);
    } catch (err) {
      Swal.close();
      setErrorMessage(err.message || "Unexpected error while publishing.");
      console.error(err);
    }
  };

  // Human review (founder 2026-10-07): approve publishes and activates in
  // one step; decline keeps it from ever running. Either overrides the AI
  // review. A member's question runs on the next evening its city needs one.
  const handleHumanReview = async (item, decision) => {
    const approve = decision === "approve";
    const ask = await Swal.fire({
      title: approve ? "Approve and publish?" : "Decline this question?",
      text: approve
        ? "It becomes active now. A member's question then runs on the next evening its city needs one."
        : "It will not run. A member who asked it sees 'not this time' and can ask another.",
      input: approve ? undefined : "text",
      // A member question's reason is shown to them (2026-11-03).
      inputPlaceholder: declineReasonPlaceholder(item),
      showCancelButton: true,
      confirmButtonText: approve ? "Approve and publish" : "Decline",
    });
    if (!ask.isConfirmed) return;
    try {
      Swal.fire({
        title: approve ? "Publishing..." : "Declining...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      const res = await reviewVaultQuestion(item._id, decision, approve ? "" : ask.value || "");
      Swal.close();
      // A member question's approval says which evening it runs and whether
      // the member was told (backend 2026-11-03): a dialog the admin closes,
      // since the banner below is cleared after a second.
      if (isMemberApprovalReply(res?.data)) Swal.fire({ text: res.data.message, confirmButtonText: "OK" });
      else if (res?.data?.status === 1) setSuccessMessage(res.data.message);
      else setErrorMessage(res?.data?.message || "Review failed");
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
        fetchData(currentPage);
      }, 1000);
    } catch (err) {
      Swal.close();
      setErrorMessage(err?.response?.data?.message || err.message || "Review failed");
    }
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        {successMessage && (
          <SuccessAlert
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={itemsPerPage}
              defaultValue="Select Number"
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10 items per page</option>
              <option value="20">20 items per page</option>
              <option value="50">50 items per page</option>
              <option value="100">100 items per page</option>
            </select>
            <form className="navbar-search" style={{ position: "relative" }}>
              <input
                type="text"
                className="bg-base h-40-px w-auto pe-30"
                name="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) =>
                  dispatch(
                    setFilters({
                      searchQuery: e.target.value,
                      currentPage: 1, // reset page on search
                    })
                  )
                }
                onKeyUp={(e) => {
                  if (e.target.value === "") {
                    dispatch(
                      setFilters({
                        searchQuery: "",
                        currentPage: 1,
                      })
                    );
                  }
                }}
                style={{
                  paddingRight: searchQuery ? "5rem" : "2.5rem", // more space if clear icon shows
                }}
              />

              {/* Search Icon */}
              <Icon
                icon="ion:search-outline"
                className="icon"
                style={{
                  position: "absolute",
                  right: searchQuery ? "3.25rem" : "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />

              {/* Clear (Close) Icon */}
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(
                      setFilters({
                        searchQuery: "",
                        currentPage: 1,
                      })
                    );
                  }}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "30%",
                    transform: "translateY(-50%)",
                    fontSize: "1.25rem",
                    color: "#000",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </form>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={tierFilter}
              onChange={(e) =>
                dispatch(
                  setFilters({
                    tierFilter: e.target.value,
                    // The bell's hidden "not reviewed yet" (reviewStatus,
                    // 2026-11-03) goes with a new tier, as with a new
                    // Status: no hidden filter stays on.
                    reviewStatusFilter: "all",
                    currentPage: 1,
                  })
                )
              }
            >
              <option value="">All Tier</option>
              <option value="iceBreaker">iceBreaker</option>
              <option value="deeperDive">deeperDive</option>
              <option value="wildCard">wildCard</option>
              <option value="dailyQuestion">Daily Questions</option>
              {/* Ask the Daily Question (2026-10-07): the questions members wrote,
                  on their own; plain "Daily Questions" is the bank only, All Tier shows both. */}
              <option value="dailyQuestion:asked">Daily Questions, member submitted</option>
              <option value="keptTogether">Kept, Together</option>
                <option value="gatekeeper">gatekeeper</option>
            </select>
            <div>
              <select
                className="form-select form-select-sm w-auto ps-12 py-6"
                style={{
                  fontSize: "16px",
                  borderRadius: "7px",
                  backgroundPositionX: "unset",
                  backgroundPosition: "right 0.75rem center",
                }}
                value={isActiveFilter}
                onChange={(e) =>
                  dispatch(
                    setFilters({
                      isActiveFilter: e.target.value,
                      currentPage: 1,
                    })
                  )
                }
              >
                <option value="all">All</option>
                <option value="true">Active</option>{" "}
                {/* map true/false to strings */}
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <select
                className="form-select form-select-sm w-auto ps-12 py-6"
                style={{
                  fontSize: "16px",
                  borderRadius: "7px",
                  backgroundPositionX: "unset",
                  backgroundPosition: "right 0.75rem center",
                }}
                value={combinedFilterValue}
                onChange={(e) => {
                  const value = e.target.value;
                  let filters = {};

                  switch (value) {
                    case "all":
                      filters = { isPublishedFilter: "all", statusFilter: "" };
                      break;
                    case "published":
                      filters = { isPublishedFilter: "true", statusFilter: "" }; // ignore status
                      break;
                    case "ai_approved":
                      filters = {
                        isPublishedFilter: "all",
                        statusFilter: "ai_approved",
                      };
                      break;
                    case "pending_ai_review":
                      filters = {
                        isPublishedFilter: "all",
                        statusFilter: "pending_ai_review,pending_review", // send both to backend
                      };
                      break;
                    case "ai_rejected":
                      filters = {
                        isPublishedFilter: "all",
                        statusFilter: "ai_rejected",
                      };
                      break;
                    default:
                      filters = { isPublishedFilter: "all", statusFilter: "" };
                  }

                  // The address's reviewStatus (the bell's "waiting for
                  // review", 2026-11-03) has no drop-down of its own, so a
                  // new choice here clears it: no hidden filter stays on.
                  dispatch(setFilters({ ...filters, reviewStatusFilter: "all", currentPage: 1 }));
                }}
              >
                <option value="all">All Vault</option>
                <option value="published">Published</option>
                <option value="ai_approved">AI Approved</option>
                <option value="ai_rejected">AI Rejected</option>
                <option value="pending_ai_review">Pending AI Review</option>
              </select>
            </div>

            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm w-auto ps-12 py-6"
                style={{
                  fontSize: "16px",
                  borderRadius: "7px",
                  backgroundPositionX: "unset",
                  backgroundPosition: "right 0.75rem center",
                }}
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
              >
                <option disable value="">
                  Export as
                </option>
                <option value="csv">CSV</option>
                <option value="xlsx">XLSX</option>
              </select>
              <button
                className="btn btn-primary"
                style={{ borderColor: "#FA69B9" }}
                onClick={handleExport}
                disabled={!exportType || exportLoading}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            </div>
            {(!shouldCheckPermissions || canDelete) && (
              <button
                className="btn btn-danger"
                onClick={handleDeleteAll}
                disabled={selectedItems.size === 0}
              >
                Delete All
              </button>
            )}
            {(!shouldCheckPermissions || canAdd) && (
              <Link
                to="/vault-questions/add-vault-question"
                className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
              >
                <Icon
                  icon="ic:baseline-plus"
                  className="icon text-xl line-height-1"
                />
                Add Vault Question
              </Link>
            )}
            {/* Bulk Upload Button */}
            {(!shouldCheckPermissions || canAdd) && (
              <div>
                <label className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2">
                  <Icon
                    icon="mdi:upload-multiple"
                    className="icon text-xl line-height-1"
                  />
                  Bulk Upload
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv, .xlsx, .xls"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            )}

            {/* Generate AI Questions Button */}
            <div>
              {(!shouldCheckPermissions || canAdd) && (
                <button
                  className="btn btn-primary bulk-edit-btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                  onClick={() => setShowModal(true)}
                >
                  <Icon
                    icon="mdi:auto-fix"
                    className="icon text-xl line-height-1"
                  />
                  Generate Questions with AI
                </button>
              )}
            </div>

            {/* Bulk Edit Button */}
            <div>
              {(!shouldCheckPermissions || canEdit) && (
                <button
                  className="btn btn-primary bulk-edit-btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                  disabled={selectedItems.length === 0}
                  onClick={handleBulkEdit}
                >
                  <Icon
                    icon="ic:baseline-edit"
                    className="icon text-xl line-height-1"
                  />
                  Bulk Edit
                </button>
              )}
            </div>

            {/* Bulk Review Button */}
            {(!shouldCheckPermissions || canReview) && (
              <div>
                <button
                  className="btn btn-primary bulk-edit-btn text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                  disabled={selectedItems.size === 0}
                  onClick={handleBulkReview}
                >
                  <Icon
                    icon="material-symbols:rate-review"
                    className="icon text-xl line-height-1"
                  />
                  Bulk Review
                </button>
              </div>
            )}
            {(!shouldCheckPermissions || canEdit) && (
              <BulkPublishButton
                selectedItems={Array.from(selectedItems)}
                refreshList={() => fetchData(currentPage)}
                clearSelection={() => setSelectedItems(new Set())}
              />
            )}
          </div>
        </div>
        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">
                    <div className="d-flex align-items-center gap-10">
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border input-form-dark"
                          type="checkbox"
                          name="checkbox"
                          // id="selectAll"
                          // onChange={handleSelectAll}
                          // checked={
                          //   selectedItems.length === items.length &&
                          //   items.length > 0
                          // }

                          ref={headerCheckboxRef}
                          checked={isHeaderChecked()}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          style={{
                            backgroundColor: isHeaderIndeterminate()
                              ? "transparent"
                              : undefined,
                            borderColor: isHeaderIndeterminate()
                              ? "#d1d5db80"
                              : undefined,
                          }}
                        />
                      </div>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("questionText")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-10">
                      Question
                      <span className="sort-colume">
                        <Icon
                          icon="mdi:arrow-up"
                          className={`arrow ${
                            sortColumn === "questionText" && sortOrder === "asc"
                              ? "active"
                              : ""
                          }`}
                        />
                        <Icon
                          icon="mdi:arrow-down"
                          className={`arrow ${
                            sortColumn === "questionText" &&
                            sortOrder === "desc"
                              ? "active"
                              : ""
                          }`}
                        />
                      </span>
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("createdAt")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-10">
                      Date{" "}
                      <span className="sort-colume">
                        <Icon
                          icon="mdi:arrow-up"
                          className={`arrow ${
                            sortColumn === "createdAt" && sortOrder === "asc"
                              ? "active"
                              : ""
                          }`}
                        />
                        <Icon
                          icon="mdi:arrow-down"
                          className={`arrow ${
                            sortColumn === "createdAt" && sortOrder === "desc"
                              ? "active"
                              : ""
                          }`}
                        />
                      </span>
                    </div>
                  </th>
                  <th scope="col">Tier</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      <span className="loader-15"></span>
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item._id}>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <div className="form-check style-check d-flex align-items-center">
                            <input
                              className="form-check-input radius-4 border border-neutral-400"
                              type="checkbox"
                              name="checkbox"
                              value={item._id}
                              checked={isSelected(item._id)}
                              onChange={() => handleSelectItem(item._id)}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        {" "}
                        <span
                          className="text-md mb-0 fw-normal text-secondary-light d-inline-block text-truncate"
                          style={{ maxWidth: "200px" }}
                          title={item?.questionText || "N/A"}
                        >
                          {item?.questionText || "N/A"}
                        </span>
                      </td>
                      <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>
                      <td>
                        {" "}
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item?.tier || "N/A"}
                        </span>
                        {item?.askedBy ? (
                          // A Daily Question a Club member wrote for their city
                          // (reviewed here, credited to "a Club member" in the app).
                          <span
                            className="text-sm fw-medium text-primary-600"
                            style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                            title={
                              item?.askedByTest
                                ? "Asked by a TEST account. It never runs in a real city, whatever its review state."
                                : "Written by a Club member through Ask the Daily Question"
                            }
                          >
                            {item?.askedByTest ? "test account question" : "member submitted"}
                          </span>
                        ) : null}
                        {/* Who asked it, for which city, when, and how long it
                            has waited (2026-11-03, AskedBy). */}
                        <AskedByInline item={item} />
                        <AskedByTimes item={item} />
                        {/* The reason typed at a human decline or approve (2026-10-09). */}
                        {item?.reviewNote ? (
                          <div
                            className={item?.reviewStatus === "rejected" ? "text-sm text-danger-600" : "text-sm text-success-600"}
                            style={{ marginTop: 4 }}
                            title={item?.reviewNote}
                          >
                            {(item?.reviewStatus === "rejected" ? "Declined: " : "Approved: ") + item.reviewNote}
                          </div>
                        ) : null}
                      </td>
                   
                      <td>
                        {!shouldCheckPermissions || canEdit ? (
                          // ✅ Clickable when user has edit permission
                          <span
                            onClick={() => {
                              if (item.reviewStatus !== "approved") {
                                setErrorMessage(
                                  "Not reviewed yet. Approve it (green check), or run the AI review (robot) first."
                                );
                                setTimeout(() => setErrorMessage(""), 3000);
                                return;
                              }
                              updateStatus(item._id, item.isActive);
                            }}
                            className={`${
                              item.isActive
                                ? "bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm"
                                : "bg-neutral-200 text-neutral-600 border border-neutral-400 px-24 py-4 radius-4 fw-medium text-sm"
                            }`}
                            style={{ cursor: "pointer" }}
                          >
                            {item?.isActive ? "Active" : "Inactive"}
                          </span>
                        ) : (
                          // 🚫 Non-clickable when user has no edit permission
                          <span
                            className={`${
                              item.isActive
                                ? "bg-success-focus text-success-600 border border-success-main opacity-50 px-24 py-4 radius-4 fw-medium text-sm"
                                : "bg-neutral-200 text-neutral-600 border border-neutral-400 opacity-50 px-24 py-4 radius-4 fw-medium text-sm"
                            }`}
                            style={{ cursor: "default" }}
                            title="You don’t have permission to change status"
                          >
                            {item?.isActive ? "Active" : "Inactive"}
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="d-flex align-items-center gap-10">
                          {(!shouldCheckPermissions || canView) && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/vault-questions/view-vault-question/${item._id}`
                                )
                              }
                              title="View"
                              className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon
                                icon="majesticons:eye-line"
                                className="icon text-xl"
                              />
                            </button>
                          )}
                          {(!shouldCheckPermissions || canEdit) && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/vault-questions/edit-vault-question/${item._id}`
                                )
                              }
                              title="Edit"
                              className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                          )}
                          {(!shouldCheckPermissions || canDelete) && (
                            <button
                              type="button"
                              title="Delete"
                              className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() =>
                                handleDeleteVaultQuestion(item._id)
                              }
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="menu-icon"
                              />
                            </button>
                          )}
                          
                          {(!shouldCheckPermissions || canReview) && (
                            <ReviewButton
                              item={item}
                              loading={loading}
                              handleAiReview={handleAiReview}
                            />
                          )}
                          {/* Human review (2026-10-07): your own approve or
                              decline, no AI needed; either overrides the AI. */}
                          {(!shouldCheckPermissions || canEdit) &&
                            !(item.reviewStatus === "approved" && item.isPublished) && (
                              <button
                                type="button"
                                title="Approve and publish (your review, no AI needed)"
                                onClick={() => handleHumanReview(item, "approve")}
                                disabled={loading}
                                style={{ border: "none", background: "transparent", marginLeft: "5px" }}
                              >
                                <Icon icon="mdi:check-decagram" width={24} height={24} color="#2e7d32" />
                              </button>
                            )}
                          {(!shouldCheckPermissions || canEdit) && item.reviewStatus !== "rejected" && (
                            <button
                              type="button"
                              title="Decline (it will not run)"
                              onClick={() => handleHumanReview(item, "decline")}
                              disabled={loading}
                              style={{ border: "none", background: "transparent", marginLeft: "5px" }}
                            >
                              <Icon icon="mdi:close-octagon-outline" width={24} height={24} color="#c62828" />
                            </button>
                          )}
                          {(!shouldCheckPermissions || canEdit) && (
                            <button
                              onClick={() => {
                                // Check if already published
                                if (item.isPublished) {
                                  setErrorMessage(
                                    "This question has already been published."
                                  );
                                  setTimeout(() => setErrorMessage(""), 3000);
                                  return;
                                }

                                // Check AI review status
                                if (
                                  item.reviewStatus === "pending" ||
                                  item.status === "pending_ai_review"
                                ) {
                                  setErrorMessage(
                                    "Not reviewed yet. Approve it (green check), or run the AI review (robot) first."
                                  );
                                  setTimeout(() => setErrorMessage(""), 3000);
                                  return;
                                }

                                if (
                                  item.reviewStatus === "rejected" ||
                                  item.status === "ai_rejected"
                                ) {
                                  setErrorMessage(
                                    "Declined. Approve it (green check) to override, or edit it and run the AI review again."
                                  );
                                  setTimeout(() => setErrorMessage(""), 3000);
                                  return;
                                }

                                // If approved, publish
                                if (
                                  item.reviewStatus === "approved" ||
                                  item.status === "ai_approved"
                                ) {
                                  handlePublish(item);
                                }
                              }}
                              disabled={
                                loading ||
                                item.isPublished ||
                                item.reviewStatus === "pending" ||
                                item.status === "pending_ai_review" ||
                                item.reviewStatus === "rejected" ||
                                item.status === "ai_rejected"
                              }
                              title={
                                item.isPublished
                                  ? "Already published"
                                  : item.reviewStatus === "pending" ||
                                    item.status === "pending_ai_review"
                                  ? "Not reviewed yet: approve it (check) or run the AI review (robot)"
                                  : item.reviewStatus === "rejected" ||
                                    item.status === "ai_rejected"
                                  ? "Declined: approve it (check) to override"
                                  : "Publish Question"
                              }
                              style={{
                                cursor:
                                  loading ||
                                  item.isPublished ||
                                  item.reviewStatus === "pending" ||
                                  item.status === "pending_ai_review" ||
                                  item.reviewStatus === "rejected" ||
                                  item.status === "ai_rejected"
                                    ? "default"
                                    : "pointer",
                                border: "none",
                                background: "transparent",
                                marginLeft: "5px",
                              }}
                            >
                              <Icon
                                icon="mdi:publish"
                                width={24}
                                height={24}
                                color={
                                  item.isPublished
                                    ? "#4caf50" // green for already published
                                    : item.reviewStatus === "pending" ||
                                      item.status === "pending_ai_review"
                                    ? "#999" // gray for pending
                                    : item.reviewStatus === "rejected" ||
                                      item.status === "ai_rejected"
                                    ? "#f44336" // red for rejected
                                    : "#1976d2" // blue for ready to publish
                                }
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            {loading ? (
              <span>Loading entries...</span>
            ) : totalItems > 0 ? (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            ) : (
              <span>No entries found</span>
            )}
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  to="#"
                  onClick={handlePrev}
                >
                  <Icon icon="ep:d-arrow-left" />
                </Link>
              </li>
              {getPaginationRange().map((page) => {
                // Return JSX for pagination items
                return (
                  <li key={page} className="page-item">
                    <Link
                      className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                        currentPage === page
                          ? "bg-primary-600 text-white"
                          : "bg-neutral-200"
                      }`}
                      to="#"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Link>
                  </li>
                );
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  to="#"
                  onClick={handleNext}
                >
                  <Icon icon="ep:d-arrow-right" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <AIQuestionPromptModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onGenerate={handleGenerateAI}
      />
    </>
  );
};

export default VaultQuestionsListLayer;
